import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getCachedPermissao } from "../utils/getCachedPermissao";
import toast from "react-hot-toast";
import { checkAutenticationByUrl } from "../utils/checkAuthByUrl";
import { getTokens, setMunicipio } from "../services/data";
import { useQueryClient } from "@tanstack/react-query";
import {
  auditoriaAcessoKey,
  obterPermissaoAuditoria,
  permiteAuditoriaAdministrativa,
} from "../services/auditoriaAcesso";

export function useCheckPermissaoSegura(apenasAdministradores = false): boolean {
  const [ok, setOk] = useState(false);
  const [authorizedScope, setAuthorizedScope] = useState("");
  const currentTokens = getTokens();
  const currentScope = `${currentTokens["x-tn-token"] || ""}:${currentTokens["x-auth-token"] || ""}`;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        await checkAutenticationByUrl();
      } catch {}

      const tokens = getTokens();
      if (!tokens["x-auth-token"] || !tokens["x-tn-token"]) {
        const cb = window.location.pathname + window.location.search;
        localStorage.setItem("callback", cb);
        navigate(`/?callback=${encodeURIComponent(cb)}`, { replace: true });
        return;
      }

      if (apenasAdministradores) {
        try {
          const acesso = await queryClient.fetchQuery({
            queryKey: auditoriaAcessoKey(tokens),
            queryFn: obterPermissaoAuditoria,
            staleTime: 0,
          });
          if (!mounted) return;
          if (permiteAuditoriaAdministrativa(acesso)) {
            if (acesso.municipio) {
              setMunicipio(acesso.municipio);
              const params = new URLSearchParams(window.location.search);
              if (params.get("municipio") !== acesso.municipio) {
                params.set("municipio", acesso.municipio);
                navigate({ search: params.toString() }, { replace: true });
              }
            }
            setAuthorizedScope(`${tokens["x-tn-token"]}:${tokens["x-auth-token"]}`);
            setOk(true);
          } else {
            toast.error("Esta auditoria está disponível apenas para ADM e ADMTENANT.");
            const municipio = new URLSearchParams(window.location.search).get("municipio");
            navigate(
              {
                pathname: "/auditoria/cadastro",
                search: municipio ? `?municipio=${encodeURIComponent(municipio)}` : "",
              },
              { replace: true },
            );
          }
        } catch {
          if (mounted) {
            toast.error("Não foi possível verificar a permissão para esta auditoria.");
            navigate("/auditoria/cadastro", { replace: true });
          }
        }
        return;
      }
      const permissoes = await getCachedPermissao();

      if (!mounted) return;

      if (!permissoes || permissoes.permissao === "LEITOR") {
        toast.error("Você não tem permissão para acessar esta funcionalidade.");
        const cb = window.location.pathname + window.location.search;
        localStorage.setItem("callback", cb);
        navigate(`/?callback=${encodeURIComponent(cb)}`, { replace: true });
      } else {
        setOk(true);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [navigate, apenasAdministradores, queryClient, currentScope]);

  return ok && (!apenasAdministradores || authorizedScope === currentScope);
}
